import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import LogicContext from "../../Context/LogicContext";
import { employeeTypeService } from "../../services/employeeType.service";
import EmployeeTypeSideScroll from "./EmployeeTypeSideScroll";
import EmployeeTypeOverview from "./EmployeeTypeOverview";
import EmployeeTypeFormModal from "./EmployeeTypeFormModal";

function isActive(record) {
  return Number(record?.statusId) === 1;
}

function employeeTypeToastMessage(message) {
  return /employee type/i.test(message) ? message : `Employee Type: ${message}`;
}

function EmployeeTypeDetailsPage() {
  const [employeeType, setEmployeeType] = useState(null);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const basePath = `/${url[1]}/${url[2]}/${url[3] || "employee-type"}`;

  useEffect(() => {
    document.title = "Employee Type Details";
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    Promise.all([
      employeeTypeService.getEmployeeType(id),
      employeeTypeService.getEmployeeTypes(),
    ])
      .then(([detail, list]) => {
        if (latestRequest.current !== requestId) return;
        setEmployeeType(detail);
        setEmployeeTypes(list);
      })
      .catch((loadError) => {
        if (latestRequest.current !== requestId) return;
        setError(
          loadError?.message
            ? employeeTypeToastMessage(loadError.message)
            : "Unable to load Employee Type details."
        );
        setEmployeeType(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });
    return () => {
      latestRequest.current += 1;
    };
  }, [id]);

  function viewEmployeeTypeDetails(employeeTypeId) {
    navigate(`${basePath}/${employeeTypeId}`);
  }

  async function refreshAfterWrite() {
    const [detail, list] = await Promise.all([
      employeeTypeService.getEmployeeType(id),
      employeeTypeService.getEmployeeTypes(),
    ]);
    setEmployeeType(detail);
    setEmployeeTypes(list);
  }

  async function refreshListAfterWrite() {
    setEmployeeTypes(await employeeTypeService.getEmployeeTypes());
  }

  async function handleEditEmployeeType({ name, statusId }) {
    setActionLoading("edit");
    try {
      const result = await employeeTypeService.updateEmployeeType(id, name, statusId);
      logicContext.showToast(employeeTypeToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeTypeToastMessage(
            refreshError?.message || "Unable to refresh Employee Type details."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateEmployeeType({ name }) {
    setActionLoading("create");
    try {
      const result = await employeeTypeService.createEmployeeType(name);
      logicContext.showToast(employeeTypeToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeTypeToastMessage(
            refreshError?.message || "Unable to refresh Employee Types."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = isActive(employeeType) ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action} Employee Type`,
      message: `Are you sure you want to ${action} the Employee Type "${employeeType.name}"?`,
    });
  }

  async function applyStatusChange() {
    setActionLoading("status");
    try {
      const result = await employeeTypeService.setEmployeeTypeStatus(
        id,
        isActive(employeeType) ? 2 : 1
      );
      logicContext.showToast(employeeTypeToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeTypeToastMessage(
            refreshError?.message || "Unable to refresh Employee Type details."
          ),
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        employeeTypeToastMessage(
          statusError?.message || "Unable to update Employee Type status."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteEmployeeType() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete Employee Type",
      message: `The Employee Type "${employeeType.name}" will be permanently deleted. Do you want to continue?`,
    });
  }

  async function applyDeleteEmployeeType() {
    setActionLoading("delete");
    try {
      const result = await employeeTypeService.deleteEmployeeType(id);
      logicContext.showToast(employeeTypeToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeTypeToastMessage(
            refreshError?.message || "Unable to refresh Employee Types."
          ),
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        employeeTypeToastMessage(
          deleteError?.message || "Unable to delete Employee Type."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteEmployeeType();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !employeeType;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={isActive(employeeType) ? 1 : "I"}
            statusName={employeeType?.statusName || "-"}
          />
        </div>
        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete Employee Type">
            <button
              onClick={handleDeleteEmployeeType}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit Employee Type">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add Employee Type">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isActive(employeeType) ? "Deactivate Employee Type" : "Activate Employee Type"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <EmployeeTypeSideScroll
            employeeTypes={employeeTypes}
            viewEmployeeTypeDetails={viewEmployeeTypeDetails}
          />
        </div>
        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Employee Type Overview
            </div>
          </div>
          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-8 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
              </div>
            ) : null}
            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">{error}</div>
            ) : null}
            {!loading && !error && !employeeType ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Employee Type not found.
              </div>
            ) : null}
            {!loading && !error && employeeType ? (
              <EmployeeTypeOverview employeeType={employeeType} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <EmployeeTypeFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? { name: employeeType?.name, statusId: employeeType?.statusId }
              : { name: "", statusId: 1 }
          }
          onSubmit={
            formMode === "edit"
              ? handleEditEmployeeType
              : handleCreateEmployeeType
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="employee-type-confirm-title"
      >
        <DialogTitle id="employee-type-confirm-title">{confirmAction?.title}</DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>Cancel Employee Type Action</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm Employee Type Action
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EmployeeTypeDetailsPage;
